import React from "react";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, LinearProgress, MenuItem, Select, SelectChangeEvent,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import PublishRoundedIcon from "@mui/icons-material/PublishRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import Title from "../components/header/Title";
import BulkUploadService from "../services/BulkUploadService";
import CategoryService from "../services/CategoryService";

const steps = ["Download Template", "Upload Excel", "Review & Validate", "Final Review", "Publish"];

const pick = (obj: any, keys: string[], fallback: any = undefined) => {
  for (const key of keys) if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
  return fallback;
};

const normalizeList = (value: any) => {
  if (Array.isArray(value)) return value;
  return value?.items || value?.content || value?.products || value?.rows || [];
};

const categoryIdOf = (cat: any) => cat?.categoryId ?? cat?.id;
const categoryLabel = (cat: any) =>
  cat?.title ?? cat?.categoryName ?? cat?.categoryTitle ?? cat?.name ?? `Category ${categoryIdOf(cat)}`;

const slug = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "category";

// Error bodies of blob requests arrive as a Blob, so the server message must be read from it.
const blobErrorMessage = async (e: any): Promise<string | undefined> => {
  const data = e?.response?.data;
  if (data instanceof Blob && data.type.includes("json")) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.error?.message || parsed?.message || (typeof parsed?.error === "string" ? parsed.error : undefined);
    } catch {
      return undefined;
    }
  }
  return undefined;
};

const friendlyError = (raw: any, fallback = "Something went wrong. Please try again."): string => {
  const text = String(
    (typeof raw === "string" ? raw : raw?.friendlyMessage || raw?.response?.data?.error?.message || raw?.message) ?? ""
  ).trim();
  if (!text) return fallback;

  const t = text.toLowerCase();
  const status = Number(/returned (\d{3})/.exec(t)?.[1] || 0);

  if (t.includes("did not return an image")) {
    return "The image link opens a web page, not a picture. Use a direct image link (it usually ends with .jpg, .png or .webp).";
  }
  if (t.includes("could not download image") || t.includes("invalid image url") || t.includes("image url must use")) {
    return "The image link could not be opened. Check that the link is correct and publicly accessible.";
  }
  if (t.includes("empty file")) return "The image file is empty. Please use a different image link.";
  if (t.includes("token expired") || status === 401 || t.includes("unauthorized")) {
    return "Your session has expired. Please log in again and retry.";
  }
  if (status === 403 || t.includes("forbidden")) return "You do not have permission to do this.";
  if (
    t.includes("could not reach") || t.includes("network error") || t.includes("fetch failed") ||
    t.includes("enotfound") || t.includes("econnrefused") || t.includes("etimedout")
  ) {
    return "We could not connect to the server. Please check your internet connection and try again. If the problem continues, contact support.";
  }
  if (t.includes("timeout")) return "The request took too long. Please try again.";
  if (t.includes("media api")) return "The product image could not be uploaded. Please check the image link and try again.";
  if (t.includes("sku") && (t.includes("already exist") || t.includes("duplicate"))) {
    return "A SKU code of this product already exists. Please use unique SKU codes.";
  }
  if (status >= 500) return "The server had a problem while saving this product. Please try again in a few minutes.";
  if (status >= 400) {
    const detail = text.includes(": ") ? text.slice(text.indexOf(": ") + 2, text.indexOf(": ") + 152) : "";
    return `The server did not accept this product${detail ? `: ${detail}` : "."}`;
  }
  return text;
};

const hasValue = (v: any) => v !== null && v !== undefined && v !== "";
const positive = (v: any) => (typeof v === "number" && v > 0 ? v : null);
const money = (v: any) => (typeof v === "number" ? v.toLocaleString() : v);

const skuSummary = (row: any) => {
  const skus = row?.payload?.skus;
  const count = Array.isArray(skus) ? skus.length : 0;
  if (!count) return "—";
  const labels: string[] = (row?.payload?.skuLabels || []).filter(Boolean);
  return (
    <Box>
      <Typography variant="body2" fontWeight={600}>{count} {count === 1 ? "SKU" : "SKUs"}</Typography>
      {labels.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {labels.slice(0, 2).join(", ")}{labels.length > 2 ? ` +${labels.length - 2} more` : ""}
        </Typography>
      )}
    </Box>
  );
};

const DetailSection: React.FC<{ title: string; items: Array<[string, any]> }> = ({ title, items }) => {
  const shown = items.filter(([, value]) => hasValue(value));
  if (!shown.length) return null;
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography fontWeight={700} sx={{ mb: 0.5 }}>{title}</Typography>
      {shown.map(([label, value]) => (
        <Box key={label} sx={{ display: "flex", gap: 2, py: 0.6 }}>
          <Typography variant="body2" color="text.secondary" sx={{ width: 170, flexShrink: 0 }}>{label}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: "break-word" }}>{value}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const ProductThumb: React.FC<{ url?: string | null }> = ({ url }) => {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [url]);
  if (!url) return <Typography variant="body2" color="text.secondary">No thumbnail provided.</Typography>;
  if (failed) {
    return (
      <Typography variant="body2" color="error.main">
        The thumbnail link does not open as an image. Use a direct image link.
      </Typography>
    );
  }
  return (
    <Box
      component="img"
      src={url}
      alt="Thumbnail"
      onError={() => setFailed(true)}
      sx={{ width: 140, height: 140, objectFit: "cover", borderRadius: "12px", border: "1px solid", borderColor: "divider" }}
    />
  );
};

const ProductDetailsDialog: React.FC<{ row: any; onClose: () => void }> = ({ row, onClose }) => {
  const product = row?.payload?.product || {};
  const skus: any[] = row?.payload?.skus || [];
  const labels: string[] = row?.payload?.skuLabels || [];
  const publishProblem = row?.status === "PUBLISH_FAILED" && row?.publishError ? friendlyError(row.publishError) : null;
  const excelRows = Array.isArray(row?.sourceRowNumbers) ? row.sourceRowNumbers.join(", ") : null;

  return (
    <Dialog open={Boolean(row)} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ fontWeight: 700 }}>{row?.title || "Product details"}</DialogTitle>
      <DialogContent dividers>
        {row && (
          <>
            {publishProblem && <Alert severity="error" sx={{ mb: 2 }}>{publishProblem}</Alert>}

            <Box sx={{ mb: 2.5 }}>
              <Typography fontWeight={700} sx={{ mb: 1 }}>Thumbnail</Typography>
              <ProductThumb url={row.thumbnail || product.thumbnail} />
            </Box>

            <DetailSection
              title="General"
              items={[
                ["Name", row.title],
                ["Category", row.categoryName],
                ["Shop", row.shopName],
                ["Brand", product.brand],
                ["Product type", product.product_type],
                ["Country of manufacture", product.country_manuf],
                ["Excel sheet", row.sourceSheet],
                ["Excel rows", excelRows],
              ]}
            />
            <DetailSection
              title="Price and stock"
              items={[
                ["Price", money(row.price)],
                ["Old price", money(product.old_price)],
                ["Special price", money(product.sp_price)],
                ["Discount (%)", positive(product.discount_in_percent)],
                ["Price after discount", product.discount_in_percent > 0 ? money(product.discounted_price) : null],
                ["Total stock", money(row.stock)],
              ]}
            />
            <DetailSection
              title="Shipping"
              items={[
                ["Weight", positive(product.weight)],
                ["Height", positive(product.height)],
                ["Size", positive(product.size)],
                ["Delivery days", positive(product.delivery_days)],
                ["Delivery charges", positive(product.delivery_charges)],
              ]}
            />
            <DetailSection
              title="Description"
              items={[
                ["Description", row.description || product.description],
                ["Product details", product.product_detail],
              ]}
            />

            <Typography fontWeight={700} sx={{ mb: 1 }}>SKUs and variants ({skus.length})</Typography>
            {skus.length ? (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SKU code</TableCell><TableCell>Variant</TableCell><TableCell>Price</TableCell>
                      <TableCell>Stock</TableCell><TableCell>Barcode</TableCell><TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {skus.map((sku: any, i: number) => (
                      <TableRow key={sku.skuCode || i}>
                        <TableCell sx={{ fontWeight: 600 }}>{sku.skuCode}</TableCell>
                        <TableCell>{labels[i] || "—"}</TableCell>
                        <TableCell>{money(sku.price)}</TableCell>
                        <TableCell>{sku.stock}</TableCell>
                        <TableCell>{sku.barcode || "—"}</TableCell>
                        <TableCell>{sku.isActive === 0 ? "Inactive" : "Active"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">No SKUs in this product.</Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

const BulkUpload: React.FC = () => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [categories, setCategories] = React.useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = React.useState<string>("");
  const [file, setFile] = React.useState<File | null>(null);
  const [batchId, setBatchId] = React.useState<string>("");
  const [reviewData, setReviewData] = React.useState<any>(null);
  const [selectedRows, setSelectedRows] = React.useState<Array<number | string>>([]);
  const [loading, setLoading] = React.useState(false);
  const [detailRow, setDetailRow] = React.useState<any>(null);
  const [message, setMessage] = React.useState<{ type: "success" | "error" | "info"; text: string } | null>(null);

  React.useEffect(() => {
    const toList = (res: any) => {
      const body = res?.data?.data ?? res?.data ?? res ?? [];
      return Array.isArray(body) ? body : body?.items || body?.content || [];
    };
    const catalogPromise = CategoryService.getCategory().then(toList).catch(() => [] as any[]);
    // Use the bulk service's category IDs and the main catalog's readable names.
    BulkUploadService.getCategories()
      .catch(() => [] as any[])
      .then(async (bulkList: any[]) => {
        const catalog = await catalogPromise;
        if (!bulkList.length) { setCategories(catalog); return; }
        const titleById: Record<string, string> = {};
        catalog.forEach((c: any) => { titleById[String(categoryIdOf(c))] = categoryLabel(c); });
        setCategories(bulkList.map((c: any) => {
          const id = String(categoryIdOf(c));
          const hasOwnName = c?.title ?? c?.categoryName ?? c?.categoryTitle ?? c?.name;
          return hasOwnName ? c : { ...c, title: titleById[id] ?? `Category ${id}` };
        }));
      });
  }, []);

  const products = normalizeList(reviewData);
  const errors = normalizeList(pick(reviewData, ["errors", "validationErrors"], []));
  const successCount = Number(pick(reviewData, ["successCount", "validCount"], products.filter((p: any) => !p.errors?.length).length) || 0);
  const errorCount = Number(pick(reviewData, ["errorCount", "invalidCount"], errors.length) || 0);

  const downloadTemplate = async () => {
    setLoading(true); setMessage(null);
    try {
      // No category selected means every category.
      const ids = selectedCategory
        ? [Number(selectedCategory)]
        : categories.map((c: any) => Number(categoryIdOf(c))).filter((id: number) => !Number.isNaN(id));
      const response = await BulkUploadService.downloadTemplate(ids);
      const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const chosen = categories.find((c: any) => String(categoryIdOf(c)) === selectedCategory);
      const fileName = chosen ? `muradiz-${slug(categoryLabel(chosen))}-template.xlsx` : "muradiz-all-categories-template.xlsx";
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      setActiveStep(1);
      setMessage({ type: "success", text: "Excel template downloaded. Fill it and continue to the upload step." });
    } catch (e: any) {
      const serverMessage = await blobErrorMessage(e);
      setMessage({ type: "error", text: friendlyError(serverMessage || e, "Could not download the Excel template.") });
    } finally { setLoading(false); }
  };

  const upload = async () => {
    if (!file) return;
    setLoading(true); setMessage(null);
    try {
      const result = await BulkUploadService.upload(file);
      const id = String(pick(result, ["batchId", "id", "uploadBatchId"], ""));
      setBatchId(id);
      setReviewData(result);
      if (id) {
        const review = await BulkUploadService.getReview(id);
        setReviewData(review);
      }
      setActiveStep(2);
      setMessage({ type: "success", text: "Excel uploaded successfully. Review the validation results below." });
    } catch (e: any) {
      setMessage({ type: "error", text: friendlyError(e, "Excel upload failed. Please check the file and try again.") });
    } finally { setLoading(false); }
  };

  const confirmReview = async () => {
    if (!batchId) return;
    setLoading(true); setMessage(null);
    try {
      await BulkUploadService.confirm(batchId, selectedRows);
      const finalData = await BulkUploadService.getFinal(batchId);
      setReviewData(finalData);
      setActiveStep(3);
      setMessage({ type: "success", text: "Review confirmed. Selected products are ready for publishing." });
    } catch (e: any) {
      setMessage({ type: "error", text: friendlyError(e, "Could not confirm the review.") });
    } finally { setLoading(false); }
  };

  const publish = async () => {
    if (!batchId) return;
    setLoading(true); setMessage(null);
    try {
      const result = await BulkUploadService.finalize(batchId);
      const uploaded = Number(result?.uploadedCount ?? 0);
      const failed = Number(result?.failedCount ?? 0);

      if (failed > 0) {
        const reasons = (result?.results || [])
          .filter((r: any) => r.status === "PUBLISH_FAILED")
          .map((r: any) => {
            const match = products.find((p: any) => String(p.id) === String(r.id));
            return `${match?.title || `Product ${r.id}`}: ${friendlyError(r.error)}`;
          })
          .join(" • ");
        setMessage({ type: "error", text: `${failed} product(s) could not be published. ${reasons}` });
        if (uploaded === 0) return;
      }

      setReviewData(result);
      setActiveStep(4);
      setMessage({ type: "success", text: `${uploaded} product(s) published successfully.` });
    } catch (e: any) {
      setMessage({ type: "error", text: friendlyError(e, "Publishing failed. Please try again.") });
    } finally { setLoading(false); }
  };

  const toggleRow = (id: number | string) => {
    setSelectedRows((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const reset = () => {
    setActiveStep(0); setFile(null); setBatchId(""); setReviewData(null); setSelectedRows([]); setMessage(null); setDetailRow(null);
  };

  const validIds = products
    .map((row: any, i: number) => ({ row, id: pick(row, ["productId", "id", "tempProductId"], i) }))
    .filter(({ row }: any) => !(row.errors?.length || row.error))
    .map(({ id }: any) => id);
  const allValidSelected = validIds.length > 0 && validIds.every((id: any) => selectedRows.includes(id));
  const toggleAll = () => setSelectedRows(allValidSelected ? [] : validIds);

  const onDrop = React.useCallback((accepted: File[]) => {
    if (accepted[0]) { setFile(accepted[0]); setMessage(null); }
  }, []);
  const { getRootProps, getInputProps, isDragActive, open: openPicker } = useDropzone({
    onDrop,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-excel": [".xls"],
    },
    onDropRejected: () => setMessage({ type: "error", text: "Only Excel files (.xlsx or .xls) are supported." }),
  });

  const cardSx = {
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(16,24,40,0.05)",
    overflow: "hidden",
  } as const;

  const CardHead: React.FC<{ icon: React.ReactNode; title: string; subtitle: string; right?: React.ReactNode }> = ({ icon, title, subtitle, right }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 3, py: 2.5, borderBottom: "1px solid", borderColor: "divider", flexWrap: "wrap" }}>
      <Box sx={{ width: 46, height: 46, borderRadius: "12px", display: "grid", placeItems: "center", bgcolor: "primary.light", color: "primary.main", flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.25 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
      </Box>
      {right}
    </Box>
  );

  const Footer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, px: 3, py: 2, bgcolor: "#F8FAFC", borderTop: "1px solid", borderColor: "divider" }}>{children}</Box>
  );

  const StatTile: React.FC<{ label: string; value: number | string; tone: "success" | "error" | "primary" }> = ({ label, value, tone }) => (
    <Box sx={{ flex: "1 1 160px", p: 2, borderRadius: "12px", border: "1px solid", borderColor: "divider", bgcolor: "#fff", borderLeft: "4px solid", borderLeftColor: `${tone}.main` }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
      <Typography variant="h5" fontWeight={800}>{value}</Typography>
    </Box>
  );

  const productName = (row: any, i: number) => pick(row, ["title", "name", "productName"], `Product ${i + 1}`);

  return (
    <Box sx={{ maxWidth: 1100 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
        <Title title="Bulk product upload" subtitle="Create products and variants from an Excel file" />
        <Button variant="outlined" onClick={reset}>Start new upload</Button>
      </Box>

      <Box sx={{ ...cardSx, mb: 2.5, px: { xs: 2, md: 4 }, py: 3, overflowX: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", minWidth: 620 }}>
          {steps.map((label, i) => {
            const done = i < activeStep;
            const current = i === activeStep;
            return (
              <React.Fragment key={label}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, width: 96, flexShrink: 0 }}>
                  <Box sx={{
                    width: 36, height: 36, borderRadius: "50%", display: "grid", placeItems: "center", fontWeight: 700, fontSize: 14,
                    bgcolor: done ? "success.main" : current ? "primary.main" : "#fff",
                    color: done || current ? "#fff" : "text.secondary",
                    border: "2px solid", borderColor: done ? "success.main" : current ? "primary.main" : "#D5DBE5",
                    boxShadow: current ? "0 0 0 5px rgba(48,51,152,0.12)" : "none",
                    transition: "all .2s ease",
                  }}>
                    {done ? <CheckRoundedIcon sx={{ fontSize: 20 }} /> : i + 1}
                  </Box>
                  <Typography variant="caption" align="center" sx={{ fontWeight: current ? 700 : 500, color: current ? "text.primary" : "text.secondary", lineHeight: 1.3 }}>{label}</Typography>
                </Box>
                {i < steps.length - 1 && (
                  <Box sx={{ flexGrow: 1, height: 2, mt: "17px", bgcolor: done ? "success.main" : "#E2E8F0", borderRadius: 2, transition: "background-color .2s ease" }} />
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {activeStep === 0 && (
        <Box sx={cardSx}>
          <CardHead icon={<DescriptionRoundedIcon />} title="Download Excel template" subtitle="Pick a category to get a template with the right variant fields" />
          <Box sx={{ p: 3, display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
            <Box>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Category</InputLabel>
                <Select value={selectedCategory} label="Category" onChange={(e: SelectChangeEvent) => setSelectedCategory(e.target.value)}>
                  <MenuItem value=""><em>All categories</em></MenuItem>
                  {categories.map((cat: any) => <MenuItem key={cat.categoryId ?? cat.id} value={String(cat.categoryId ?? cat.id)}>{categoryLabel(cat)}</MenuItem>)}
                </Select>
              </FormControl>
              <Button disabled={loading} variant="contained" size="large" startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DownloadRoundedIcon />} onClick={downloadTemplate}>Download template</Button>
              <Box sx={{ mt: 2 }}>
                <Button variant="text" onClick={() => setActiveStep(1)} endIcon={<ArrowForwardRoundedIcon />}>I already have a template</Button>
              </Box>
            </Box>
            <Box sx={{ p: 2.5, bgcolor: "#F8FAFC", border: "1px solid", borderColor: "divider", borderRadius: "12px" }}>
              <Typography fontWeight={700} mb={1.5}>The file includes</Typography>
              {["Product fields and category-specific variants", "Dropdown values for easy filling", "Instructions and validation-friendly columns", "Internal category and variant IDs stay hidden"].map((t) => (
                <Box key={t} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", mb: 1.1 }}>
                  <TaskAltRoundedIcon sx={{ fontSize: 18, color: "success.main", mt: "2px" }} />
                  <Typography variant="body2" color="text.secondary">{t}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {activeStep === 1 && (
        <Box sx={cardSx}>
          <CardHead icon={<CloudUploadRoundedIcon />} title="Upload completed Excel" subtitle="Fill the template without changing its headers or sheet structure" />
          <Box sx={{ p: 3 }}>
            <Box
              {...getRootProps()}
              sx={{
                p: { xs: 3, md: 5 }, textAlign: "center", borderRadius: "14px",
                border: "2px dashed", borderColor: isDragActive ? "primary.main" : "#CBD5E1",
                bgcolor: isDragActive ? "primary.light" : "#F8FAFC",
                transition: "all .15s ease",
              }}
            >
              <input {...getInputProps()} />
              <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: "primary.light", color: "primary.main", display: "grid", placeItems: "center", mx: "auto", mb: 1.5 }}>
                <CloudUploadRoundedIcon sx={{ fontSize: 32 }} />
              </Box>
              <Typography fontWeight={700}>{isDragActive ? "Drop the file here" : "Drag and drop your Excel file here"}</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>Supported formats: .xlsx, .xls</Typography>
              <Button variant="outlined" onClick={openPicker}>Browse files</Button>
            </Box>

            {file && (
              <Box sx={{ mt: 2, p: 1.5, pl: 2, display: "flex", alignItems: "center", gap: 1.5, border: "1px solid", borderColor: "divider", borderRadius: "12px" }}>
                <InsertDriveFileRoundedIcon sx={{ color: "success.main" }} />
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>{file.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{(file.size / 1024).toFixed(1)} KB</Typography>
                </Box>
                <IconButton size="small" aria-label="Remove file" onClick={() => setFile(null)}><CloseRoundedIcon fontSize="small" /></IconButton>
              </Box>
            )}
          </Box>
          <Footer>
            <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => setActiveStep(0)}>Back</Button>
            <Button variant="contained" disabled={!file || loading} onClick={upload} endIcon={<ArrowForwardRoundedIcon />}>Upload and validate</Button>
          </Footer>
        </Box>
      )}

      {activeStep === 2 && (
        <Box sx={cardSx}>
          <CardHead icon={<ListAltRoundedIcon />} title="Review and validate" subtitle={`Batch ${batchId || "pending"}`} />
          <Box sx={{ p: 3, pb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <StatTile label="Valid products" value={successCount} tone="success" />
            <StatTile label="With errors" value={errorCount} tone="error" />
            <StatTile label="Selected" value={selectedRows.length} tone="primary" />
          </Box>
          {products.length ? (
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ "& td, & th": { py: 1.25 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ pl: 1.5 }}>
                      <Checkbox checked={allValidSelected} indeterminate={!allValidSelected && selectedRows.length > 0} onChange={toggleAll} disabled={!validIds.length} inputProps={{ "aria-label": "Select all valid products" }} />
                    </TableCell>
                    <TableCell>Product</TableCell><TableCell>Status</TableCell><TableCell>SKU / variants</TableCell><TableCell>Message</TableCell><TableCell align="right" sx={{ pr: 3 }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((row: any, i: number) => {
                    const id = pick(row, ["productId", "id", "tempProductId"], i);
                    const invalid = Boolean(row.errors?.length || row.error);
                    const publishFailed = row.status === "PUBLISH_FAILED";
                    const problem = invalid
                      ? friendlyError(row.error || row.errors.join(", "))
                      : publishFailed
                        ? friendlyError(row.publishError, "Publishing failed. You can select it and try again.")
                        : null;
                    return (
                      <TableRow key={id} hover selected={selectedRows.includes(id)} onClick={() => setDetailRow(row)} sx={{ cursor: "pointer" }}>
                        <TableCell padding="checkbox" sx={{ pl: 1.5 }} onClick={(e) => e.stopPropagation()}><Checkbox checked={selectedRows.includes(id)} onChange={() => toggleRow(id)} disabled={invalid} /></TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{productName(row, i)}</TableCell>
                        <TableCell><Chip size="small" label={invalid ? "Needs attention" : publishFailed ? "Publish failed" : "Valid"} color={invalid || publishFailed ? "error" : "success"} variant="outlined" /></TableCell>
                        <TableCell>{skuSummary(row)}</TableCell>
                        <TableCell sx={{ color: problem ? "error.main" : "text.secondary" }}>{problem || "Ready for review"}</TableCell>
                        <TableCell align="right" sx={{ pr: 2 }}><Button size="small">View</Button></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Box sx={{ m: 3, mt: 1, p: 5, textAlign: "center", bgcolor: "#F8FAFC", border: "1px dashed #CBD5E1", borderRadius: "12px" }}>
              <Typography fontWeight={700}>No review rows returned</Typography>
              <Typography variant="body2" color="text.secondary">If the upload returned a batch ID, go back and upload again, or check the backend response.</Typography>
            </Box>
          )}
          <Footer>
            <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => setActiveStep(1)}>Back</Button>
            <Button variant="contained" disabled={!batchId || !selectedRows.length || loading} onClick={confirmReview} endIcon={<ArrowForwardRoundedIcon />}>Confirm selected ({selectedRows.length})</Button>
          </Footer>
        </Box>
      )}

      {activeStep === 3 && (
        <Box sx={cardSx}>
          <CardHead icon={<TaskAltRoundedIcon />} title="Final review" subtitle="These products passed review and are ready to publish" />
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ "& td, & th": { py: 1.25 } }}>
              <TableHead><TableRow><TableCell sx={{ pl: 3 }}>Product</TableCell><TableCell>Status</TableCell><TableCell>SKU / variants</TableCell><TableCell align="right" sx={{ pr: 3 }}>Details</TableCell></TableRow></TableHead>
              <TableBody>
                {products.length ? products.map((row: any, i: number) => (
                  <TableRow key={i} hover onClick={() => setDetailRow(row)} sx={{ cursor: "pointer" }}>
                    <TableCell sx={{ pl: 3, fontWeight: 600 }}>{productName(row, i)}</TableCell>
                    <TableCell><Chip size="small" color="success" variant="outlined" label="Ready to publish" /></TableCell>
                    <TableCell>{skuSummary(row)}</TableCell>
                    <TableCell align="right" sx={{ pr: 2 }}><Button size="small">View</Button></TableCell>
                  </TableRow>
                )) : <TableRow><TableCell colSpan={4} sx={{ pl: 3 }}>Selected products are ready.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Box>
          <Footer>
            <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => setActiveStep(2)}>Back</Button>
            <Button variant="contained" disabled={!batchId || loading} startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PublishRoundedIcon />} onClick={publish}>Publish products</Button>
          </Footer>
        </Box>
      )}

      {activeStep === 4 && (
        <Box sx={{ ...cardSx, p: { xs: 3, md: 6 }, textAlign: "center" }}>
          <Box sx={{ width: 84, height: 84, borderRadius: "50%", bgcolor: "#DCFCE7", display: "grid", placeItems: "center", mx: "auto", mb: 2 }}>
            <CheckCircleRoundedIcon sx={{ fontSize: 52, color: "success.main" }} />
          </Box>
          <Typography variant="h5" fontWeight={800}>Bulk upload completed</Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 560, mx: "auto", mt: 1, mb: 3 }}>Your products were sent for publishing. Check the result below or start another upload.</Typography>
          <Box sx={{ p: 2, bgcolor: "#F8FAFC", border: "1px solid", borderColor: "divider", borderRadius: "12px", mb: 3, textAlign: "left", maxWidth: 420, mx: "auto" }}>
            <Typography variant="body2"><b>Batch ID:</b> {batchId || "—"}</Typography>
            <Typography variant="body2"><b>Result:</b> {`${reviewData?.uploadedCount ?? 0} published, ${reviewData?.failedCount ?? 0} failed`}</Typography>
          </Box>
          <Button variant="contained" size="large" onClick={reset}>Start another upload</Button>
        </Box>
      )}
      <ProductDetailsDialog row={detailRow} onClose={() => setDetailRow(null)} />
    </Box>
  );
};

export default BulkUpload;